import { Link } from "react-router";
import { ExternalLink } from "lucide-react";
import type { HubspotPartnerDealRelationship } from "@colab/shared";
import { HUBSPOT_PARTNER_DEAL_RELATIONSHIP_LABEL } from "@colab/shared";
import { formatDate } from "@/lib/utils";
import type { AdminKanbanDeal } from "./use-admin-deals";

// SPA port of /app/admin/deals/_components/DealCard.tsx. Click anywhere on
// the card opens the drawer via `?deal=<hubspot_deal_id>`; the partner
// sub-link routes to /admin/partners/:id and the HubSpot external link
// opens a new tab. Both stop click-bubbling so the parent <Link> wrap
// doesn't fire.

type Props = {
  deal: AdminKanbanDeal;
  onSelect: () => void;
};

export function DealCard({ deal, onSelect }: Props) {
  return (
    <article className="group relative rounded-md border bg-background p-3 text-sm shadow-sm transition hover:border-foreground/30 hover:shadow">
      <button
        type="button"
        onClick={onSelect}
        aria-label={`Open drawer for ${deal.deal_name || "deal"}`}
        className="absolute inset-0 z-0 rounded-md"
      />
      <div className="pointer-events-none relative z-10">
        <div className="flex items-start justify-between gap-2">
          <span className="min-w-0 truncate font-medium text-foreground group-hover:text-accent">
            {deal.deal_name || "(unnamed deal)"}
          </span>
          <a
            href={deal.hubspot_url}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(e) => e.stopPropagation()}
            aria-label="Open in HubSpot"
            title="Open in HubSpot"
            className="pointer-events-auto shrink-0 rounded p-0.5 text-muted-foreground hover:text-accent"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>

        <ul className="mt-2 space-y-1">
          {deal.attributions.map((a, i) => (
            <li
              key={`${a.partner_id}-${a.relationship}-${i}`}
              className="flex items-center justify-between gap-2 text-[11px]"
            >
              <Link
                to={`/admin/partners/${a.partner_id}`}
                onClick={(e) => e.stopPropagation()}
                className="pointer-events-auto truncate font-medium text-foreground hover:text-accent"
              >
                {a.partner_name}
              </Link>
              <span
                className={
                  "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium " +
                  relationshipBadgeClass(a.relationship)
                }
              >
                {HUBSPOT_PARTNER_DEAL_RELATIONSHIP_LABEL[a.relationship]}
              </span>
            </li>
          ))}
        </ul>

        <footer className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
          <span className="tabular-nums">
            {deal.deal_amount != null
              ? formatAmount(deal.deal_amount, deal.deal_currency_code ?? "EUR")
              : "—"}
          </span>
          <span>{formatDate(deal.deal_close_date)}</span>
        </footer>
      </div>
    </article>
  );
}

function relationshipBadgeClass(
  rel: HubspotPartnerDealRelationship,
): string {
  switch (rel) {
    case "referred_to_us":
      return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
    case "referred_to_them":
      return "bg-blue-500/10 text-blue-600 dark:text-blue-400";
    case "influenced":
      return "bg-amber-500/10 text-amber-600 dark:text-amber-400";
  }
}

function formatAmount(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value.toLocaleString("en-IE", { maximumFractionDigits: 0 })} ${currency}`;
  }
}
